/**
 * ChatInput
 * Multi-line textarea that expands up to 5 lines with character limit
 * warnings and keyboard shortcut support.
 *
 * - Enter -> send
 * - Shift+Enter -> newline
 * - Yellow border at 4500 chars, red at 4900, blocked at 5000
 * - Talk with Martha button (Mic icon -- starts ConvAI voice session)
 * - Magic Prompt Refine button (Sparkles icon -> calls /api/refine)
 * - Contextual placeholder based on current page
 * - Text labels on wider sidebar (>400px)
 */

import { useRef, useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Send, Square, Sparkles, Zap, Mic, MicOff } from 'lucide-react';
import { useRefinePrompt } from '../../hooks/useRefinePrompt';
import { useMentionAutocomplete } from '../topics/input/hooks/useMentionAutocomplete';
import { MentionAutocomplete } from '../topics/input/MentionAutocomplete';
import { PalettePortal } from '../topics/input/PalettePortal';
import { getBrandColor } from '../../utils/brandColor';
import type { TalkMessage } from '../../hooks/useTalkSession';

const MAX_CHARS = 5_000;
const WARN_YELLOW = 4_500;
const WARN_RED = 4_900;
const MAX_HEIGHT_PX = 160; // ~6-7 lines at default line-height

interface ChatInputProps {
  onSend: (content: string) => void;
  disabled?: boolean;
  /** Conversation messages for refine context (last 6 used) */
  messages?: TalkMessage[];
  /** Agent mode toggle state */
  agentMode?: boolean;
  onToggleAgentMode?: () => void;
  /** Whether Martha is currently streaming a response */
  isStreaming?: boolean;
  /** Cancel the current stream */
  onCancel?: () => void;
  /** Whether a ConvAI voice session is active */
  isVoiceActive?: boolean;
  /** Start a bidirectional voice conversation */
  onStartVoice?: () => void;
  /** Stop the voice conversation */
  onStopVoice?: () => void;
}

function getRingClass(count: number): string {
  if (count >= WARN_RED) return 'focus-within:ring-red-400/50 focus-within:border-red-300';
  if (count >= WARN_YELLOW) return 'focus-within:ring-yellow-400/50 focus-within:border-yellow-300';
  return 'focus-within:ring-coral-200/50 focus-within:border-coral-300';
}

/** Contextual placeholder based on current route */
function getPlaceholder(pathname: string): string {
  const pathAfterWs = pathname.replace(/^\/w\/[^/]+/, '');

  if (pathAfterWs.includes('/documents/') || pathAfterWs.includes('/do/')) return 'Ask about this document...';
  if (pathAfterWs.includes('/documents') || pathAfterWs.includes('/do')) return 'Ask about documentation...';
  if (pathAfterWs.includes('/bo/') || pathAfterWs.includes('/bo')) return 'Ask about this board...';
  if (pathAfterWs.includes('/sp')) return 'Ask about sprint readiness...';
  if (pathAfterWs.includes('/ep')) return 'Ask about epics...';
  if (pathAfterWs.includes('/tl')) return 'Ask about telemetry...';
  if (pathAfterWs.includes('/me')) return 'Ask Martha to help write...';
  if (pathAfterWs.startsWith('/ar')) return 'Ask about system health...';
  if (pathAfterWs.startsWith('/su')) return 'Ask about support...';
  if (pathAfterWs.startsWith('/sm')) return 'Ask about sales & marketing...';

  return 'Ask Martha anything...';
}

export function ChatInput({
  onSend,
  disabled = false,
  messages = [],
  agentMode = false,
  onToggleAgentMode,
  isStreaming = false,
  onCancel,
  isVoiceActive = false,
  onStartVoice,
  onStopVoice,
}: ChatInputProps) {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const charCount = value.length;
  const canSend = value.trim().length > 0 && !disabled && charCount < MAX_CHARS;
  const location = useLocation();
  const placeholder = getPlaceholder(location.pathname);
  const mention = useMentionAutocomplete();

  const {
    isRefining,
    result: refineResult,
    refine,
    clearResult: clearRefineResult,
  } = useRefinePrompt();

  // When refine result arrives, replace the textarea value
  useEffect(() => {
    if (refineResult && refineResult.rewritten !== refineResult.original) {
      setValue(refineResult.rewritten.slice(0, MAX_CHARS));
      clearRefineResult();
      if (textareaRef.current) {
        const el = textareaRef.current;
        requestAnimationFrame(() => {
          el.style.height = 'auto';
          el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT_PX)}px`;
        });
      }
    } else if (refineResult) {
      // No change — clear silently
      clearRefineResult();
    }
  }, [refineResult, clearRefineResult]);

  const resizeTextarea = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT_PX)}px`;
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value.slice(0, MAX_CHARS);
    setValue(next);
    resizeTextarea(e.target);
    const cursorPos = e.target.selectionStart ?? next.length;
    mention.handleTextChange(next, cursorPos);
  };

  const handleSelectMention = useCallback((candidate: Parameters<typeof mention.insertMention>[0]) => {
    const cursorPos = textareaRef.current?.selectionStart ?? value.length;
    const newMsg = mention.insertMention(candidate, value, mention.anchorPos, cursorPos);
    setValue(newMsg.slice(0, MAX_CHARS));
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        const pos = mention.anchorPos + `@[${candidate.name}](${candidate.id}) `.length;
        textareaRef.current.setSelectionRange(pos, pos);
        textareaRef.current.focus();
        resizeTextarea(textareaRef.current);
      }
    });
  }, [mention, value]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Mention navigation takes priority
    if (mention.isOpen && mention.filteredCandidates.length > 0) {
      const cursorPos = textareaRef.current?.selectionStart ?? value.length;
      const result = mention.handleKeyDown(e.key, value, mention.anchorPos, cursorPos);
      if (result.consumed) {
        e.preventDefault();
        if (result.newMessage !== undefined) setValue(result.newMessage.slice(0, MAX_CHARS));
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.altKey) {
      e.preventDefault();
      handleSend();
    }
    // Option+Enter: insert newline (same as Shift+Enter)
    if (e.key === 'Enter' && e.altKey) {
      e.preventDefault();
      const ta = textareaRef.current;
      if (ta) {
        const { selectionStart, selectionEnd } = ta;
        const next = value.slice(0, selectionStart) + '\n' + value.slice(selectionEnd);
        setValue(next.slice(0, MAX_CHARS));
        requestAnimationFrame(() => {
          ta.selectionStart = ta.selectionEnd = selectionStart + 1;
          resizeTextarea(ta);
        });
      }
    }
  };

  const handleSend = () => {
    if (!canSend) return;
    const trimmed = value.trim();
    setValue('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    onSend(trimmed);
  };

  const handleRefine = () => {
    if (!value.trim() || isRefining) return;
    const history = messages
      .filter((m) => m.role !== 'system')
      .slice(-6)
      .map((m) => ({ role: m.role, content: m.content }));
    refine(value, history);
  };

  const hasInputText = value.trim().length > 0;
  const canRefine = hasInputText && !isRefining && !disabled;

  return (
    <div ref={containerRef} className="px-4 py-3 bg-white border-t border-neutral-200/60 flex-shrink-0 shadow-[0_-1px_3px_rgba(0,0,0,0.03)]">
      {mention.isOpen && mention.filteredCandidates.length > 0 && (
        <PalettePortal anchorRef={containerRef}>
          <MentionAutocomplete
            candidates={mention.filteredCandidates}
            selectedIndex={mention.selectedIndex}
            accentColor={getBrandColor('coral-500')}
            onSelect={handleSelectMention}
            onHover={mention.setSelectedIndex}
          />
        </PalettePortal>
      )}
      {/* Magic border wrapper — animated gradient border while refining */}
      <div className="relative">
        {isRefining && (
          <div className="absolute -inset-[2px] rounded-[18px] z-0 pointer-events-none overflow-hidden">
            {/* Moving gradient border */}
            <div
              className="absolute inset-0 rounded-[18px] w-full h-full opacity-50"
              style={{
                background: 'linear-gradient(90deg, rgb(var(--c-coral-500, 217 127 111)), rgb(var(--c-coral-400, 232 168 124)), rgb(var(--c-coral-600, 198 102 86)), rgb(var(--c-coral-500, 217 127 111)))',
                backgroundSize: '300% 100%',
                animation: 'magicBorderFlow 3s linear infinite',
              }}
            />
            {/* Glow layer */}
            <div
              className="absolute inset-0 rounded-[18px] w-full h-full opacity-20 blur-lg"
              style={{
                background: 'linear-gradient(90deg, rgb(var(--c-coral-500, 217 127 111)), rgb(var(--c-coral-400, 232 168 124)), rgb(var(--c-coral-600, 198 102 86)), rgb(var(--c-coral-500, 217 127 111)))',
                backgroundSize: '300% 100%',
                animation: 'magicBorderFlow 3s linear infinite',
              }}
            />
          </div>
        )}

        <div
          className={`relative z-10 flex flex-col rounded-2xl border
                      bg-neutral-50/50
                      focus-within:ring-1 focus-within:bg-white
                      transition-all duration-200
                      ${isRefining
                        ? 'border-transparent bg-white shadow-[0_0_30px_-8px_rgb(var(--c-coral-500,_217_127_111)_/_0.4)]'
                        : `border-neutral-200/80 ${getRingClass(charCount)}`
                      }`}
        >
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={isRefining ? 'Refining your prompt...' : placeholder}
            disabled={disabled || isRefining}
            rows={2}
            aria-label="Chat message"
            className="w-full px-4 py-3.5 bg-transparent border-0 resize-none text-sm min-h-[56px]
                       text-neutral-800
                       placeholder:text-neutral-400
                       focus:outline-none
                       disabled:opacity-40 overflow-y-auto"
            style={{ maxHeight: `${MAX_HEIGHT_PX}px` }}
          />
          <div className="flex items-center gap-1 px-2 pb-2 justify-end">
            {/* Agent mode toggle */}
            {onToggleAgentMode && (
              <button
                type="button"
                onClick={onToggleAgentMode}
                aria-label={agentMode ? 'Disable agent mode' : 'Enable agent mode'}
                title={agentMode ? 'Agent mode on -- extended reasoning' : 'Agent mode -- deeper analysis'}
                className={`flex items-center gap-1 px-1.5 py-1 rounded-xl flex-shrink-0 transition-all duration-200 text-xs font-medium
                  ${agentMode
                    ? 'text-violet-600 bg-violet-50 shadow-sm border border-violet-200/60'
                    : 'text-neutral-300 hover:text-violet-500 hover:bg-violet-50'
                  }`}
              >
                <Zap size={14} />
              </button>
            )}

            {/* Refine / Magic Prompt button */}
            <button
              type="button"
              onClick={handleRefine}
              disabled={!canRefine}
              aria-label="Refine prompt"
              title={hasInputText ? 'Refine prompt for better results' : 'Type a message first'}
              className={`flex items-center gap-1 px-1.5 py-1 rounded-xl flex-shrink-0 transition-all duration-200 text-xs font-medium
                ${isRefining
                  ? 'text-coral-600 bg-coral-50 shadow-sm'
                  : canRefine
                    ? 'text-coral-400 hover:text-coral-600 hover:bg-coral-50'
                    : 'text-neutral-200 cursor-not-allowed'
                }`}
              style={isRefining ? {
                animation: 'pulse 1.5s ease-in-out infinite',
              } : undefined}
            >
              <Sparkles size={14} />
            </button>

            {/* Voice call button — starts/stops ConvAI bidirectional voice session */}
            {onStartVoice && (
              <button
                type="button"
                onClick={isVoiceActive ? onStopVoice : onStartVoice}
                aria-label={isVoiceActive ? 'End voice session' : 'Start voice session'}
                title={isVoiceActive ? 'End voice call' : 'Talk with Martha'}
                className={`flex items-center gap-1 px-1.5 py-1 rounded-xl flex-shrink-0 transition-all duration-200 text-xs font-medium
                  ${isVoiceActive
                    ? 'text-white bg-red-500 hover:bg-red-600 shadow-md'
                    : 'text-coral-500 hover:text-coral-600 hover:bg-coral-50'
                  }`}
                style={isVoiceActive ? {
                  animation: 'pulse 2s ease-in-out infinite',
                } : undefined}
              >
                {isVoiceActive ? <MicOff size={14} /> : <Mic size={14} />}
              </button>
            )}

            {/* Send / Stop */}
            {isStreaming && onCancel ? (
              <button
                type="button"
                onClick={onCancel}
                aria-label="Stop response"
                title="Stop generating"
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl flex-shrink-0
                           transition-all duration-200 text-xs font-medium
                           text-white bg-neutral-700 hover:bg-neutral-800
                           shadow-sm hover:shadow-md active:scale-95"
              >
                <Square size={13} fill="currentColor" />
                <span>Stop</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSend}
                disabled={!canSend}
                aria-label="Send message"
                title="Send message"
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-xl flex-shrink-0
                           transition-all duration-200 text-xs font-medium
                           disabled:bg-neutral-100 disabled:text-neutral-300 disabled:cursor-not-allowed
                           enabled:text-white enabled:shadow-warm-sm enabled:hover:shadow-warm-md
                           enabled:active:scale-95"
                style={canSend ? {
                  background: 'linear-gradient(135deg, rgb(var(--c-coral-500, 217 127 111)) 0%, rgb(var(--c-coral-600, 198 102 86)) 100%)',
                } : undefined}
              >
                <Send size={13} />
                <span>Send</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {charCount >= WARN_YELLOW && (
        <p className={`text-[10px] mt-1.5 px-1 ${
          charCount >= WARN_RED ? 'text-red-500' : 'text-yellow-600'
        }`}>
          {MAX_CHARS - charCount} characters remaining
        </p>
      )}
    </div>
  );
}
